import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BarChartIcon from '@mui/icons-material/BarChart';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import {
    Alert, Box, Button, Collapse, Grid, IconButton, LinearProgress, Paper, Table, TableBody, TableCell,
    TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import React from 'react';
import {
    Bar, BarChart, CartesianGrid, LabelList, Legend, ReferenceLine, ResponsiveContainer,
    Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';

import { DataAPIContext } from '../api';
import { GET_ONE } from '../api/dataProvider';
import { isOk, serverErrorMessage } from '../utils/errors';
import {
    bucketLabel, changePercent, defaultThreshold, pickScaleBlock, reviewPeriod, share,
} from './reviewInsightsUtils';

// Charts above the Ratings table (CP-5220). Colors: a calmer blue and a red, picked from candidates (2026-10-29)
// and validated as a pair on the white card (CVD ΔE 19.2, protan; normal vision 32.2; both >= 3:1 contrast). The
// design's green/pink pair failed for red-green color blindness (ΔE 2.7), so positive is blue, not green.
const SERIES = '#256abf';
const POSITIVE = '#256abf';
const NEGATIVE = '#e34948';
const TRACK = '#d3e1f2'; // the positive meter's track: SERIES at 20% on white
const GRID = '#e1e0d9';
const AXIS = '#c3c2b7';
const MUTED = '#898781';
const TEXT_SECONDARY = '#52514e';
const GOOD_TEXT = '#006300';
const BAD_TEXT = '#d03b3b';
const MAX_BARS_SHOWN = 12;
const COLLAPSED_KEY = 'review_insights_collapsed';

const cardSx = { border: '1px solid #D8D8D8', borderRadius: '12px', p: 2, bgcolor: '#fff', height: '100%', boxSizing: 'border-box' };

const readCollapsed = () => {
    try {
        return window.localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
        return false;
    }
};

const writeCollapsed = (value) => {
    try {
        window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
    } catch {
        // per-browser convenience only
    }
};

const formatCount = (n) => Number(n || 0).toLocaleString('en-US');

const DataTable = ({ columns, rows }) => (
    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    {columns.map((c) => <TableCell key={c.key} align={c.align || 'left'}>{c.label}</TableCell>)}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, i) => (
                    <TableRow key={i}>
                        {columns.map((c) => (
                            <TableCell key={c.key} align={c.align || 'left'} sx={{ fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined }}>
                                {c.format ? c.format(row[c.key], row) : row[c.key]}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

const ChartCard = ({ title, subtitle, table, children }) => {
    const [asTable, setAsTable] = React.useState(false);
    return (
        <Box sx={cardSx}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
                <Box>
                    <Typography fontWeight={500}>{title}</Typography>
                    {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
                </Box>
                {table && (
                    <Tooltip title={asTable ? 'Show chart' : 'Show as table'}>
                        <IconButton size="small" aria-label={asTable ? `Show ${title} as chart` : `Show ${title} as table`} onClick={() => setAsTable((v) => !v)}>
                            {asTable ? <BarChartIcon fontSize="small" /> : <TableChartOutlinedIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {asTable && table ? <DataTable {...table} /> : children}
        </Box>
    );
};

const Delta = ({ value, goodWhenUp, comparedTo }) => {
    if (value == null) return null;
    const up = value > 0;
    const flat = value === 0;
    let color = TEXT_SECONDARY;
    if (!flat && goodWhenUp != null) color = up === goodWhenUp ? GOOD_TEXT : BAD_TEXT;
    const Icon = up ? ArrowUpwardIcon : ArrowDownwardIcon;
    return (
        <Box display="flex" alignItems="center" gap={0.25} mt={0.75}>
            {!flat && <Icon sx={{ fontSize: 14, color }} />}
            <Typography variant="body2" sx={{ color }}>
                {flat ? 'No change' : `${up ? '+' : '−'}${Math.abs(value)}%`}
            </Typography>
            <Typography variant="body2" color="text.secondary">&nbsp;vs {comparedTo}</Typography>
        </Box>
    );
};

const StatTile = ({ label, value, unit, icon, detail, meter, delta, goodWhenUp, comparedTo }) => (
    <Box sx={cardSx}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
            <Typography fontSize={32} fontWeight={600} lineHeight={1.15}>{value}</Typography>
            {unit && <Typography color="text.secondary">{unit}</Typography>}
            {icon}
        </Box>
        {detail && <Typography variant="body2" color="text.secondary">{detail}</Typography>}
        {meter != null && (
            <Box role="meter" aria-valuenow={meter} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${meter}%`}
                sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: TRACK, overflow: 'hidden' }}>
                <Box sx={{ width: `${meter}%`, height: '100%', bgcolor: SERIES, borderRadius: 3 }} />
            </Box>
        )}
        <Delta value={delta} goodWhenUp={goodWhenUp} comparedTo={comparedTo} />
    </Box>
);

const TooltipBox = ({ title, lines }) => (
    <Paper elevation={3} sx={{ p: 1, maxWidth: 280 }}>
        <Typography fontSize={13} fontWeight={500}>{title}</Typography>
        {lines.map(([dot, text]) => (
            <Box key={text} display="flex" alignItems="center" gap={0.75}>
                {dot && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot }} />}
                <Typography fontSize={12} color={TEXT_SECONDARY}>{text}</Typography>
            </Box>
        ))}
    </Paper>
);

const FaceTick = ({ x, y, payload }) => (
    <image href={`/rating-emojis/rating-${payload.value}.svg`} x={x - 24} y={y - 10} width={20} height={20}>
        <title>{`Rating ${payload.value}`}</title>
    </image>
);

const NameTick = ({ x, y, payload }) => {
    const name = String(payload.value ?? '');
    const short = name.length > 30 ? `${name.slice(0, 29)}…` : name;
    return (
        <text x={x} y={y} dy={4} textAnchor="end" fill={TEXT_SECONDARY} fontSize={12}>
            <title>{name}</title>
            {short}
        </text>
    );
};

const ScoreBreakdown = ({ block }) => {
    const smiley = block.scale === 5;
    const rows = block.distribution.map((d) => ({ ...d, pct: share(d.count, block.count) }));
    const data = smiley ? [...rows].reverse() : rows;
    const tooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const r = payload[0].payload;
        return <TooltipBox title={`Rating ${r.score}`} lines={[[null, `${formatCount(r.count)} reviews (${r.pct}%)`]]} />;
    };
    return (
        <ResponsiveContainer width="100%" height={smiley ? 190 : 210}>
            {smiley ? (
                <BarChart data={data} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
                    <XAxis type="number" hide domain={[0, 100]} />
                    <YAxis type="category" dataKey="score" width={32} tick={<FaceTick />} axisLine={false} tickLine={false} interval={0} />
                    <ChartTooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={tooltip} />
                    <Bar dataKey="pct" fill={SERIES} barSize={14} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                        <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fill: TEXT_SECONDARY, fontSize: 12 }} />
                    </Bar>
                </BarChart>
            ) : (
                <BarChart data={data} margin={{ top: 20, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID} />
                    <XAxis dataKey="score" tick={{ fill: MUTED, fontSize: 12 }} tickLine={false} axisLine={{ stroke: AXIS }} />
                    <YAxis hide domain={[0, 'dataMax']} />
                    <ChartTooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={tooltip} />
                    <Bar dataKey="pct" fill={SERIES} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                        <LabelList dataKey="pct" position="top" formatter={(v) => `${v}%`} style={{ fill: TEXT_SECONDARY, fontSize: 12 }} />
                    </Bar>
                </BarChart>
            )}
        </ResponsiveContainer>
    );
};

const Trend = ({ rows }) => {
    const tooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const r = payload[0].payload;
        return <TooltipBox title={r.label} lines={[[POSITIVE, `Positive: ${formatCount(r.positive)}`], [NEGATIVE, `Negative: ${formatCount(r.negative)}`]]} />;
    };
    // At most ~8 date labels, so they never collide on a narrow card
    const labelEvery = Math.max(1, Math.ceil(rows.length / 8));
    return (
        <ResponsiveContainer width="100%" height={260}>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 12 }} tickLine={false} axisLine={{ stroke: AXIS }} interval={labelEvery - 1} />
                <YAxis allowDecimals={false} tick={{ fill: MUTED, fontSize: 12 }} tickLine={false} axisLine={false} width={44}
                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 100) / 10}K` : v)} />
                <ChartTooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={tooltip} />
                <Legend verticalAlign="top" align="right" height={28} iconType="circle" iconSize={8}
                    formatter={(value) => <span style={{ color: TEXT_SECONDARY, fontSize: 12 }}>{value}</span>} />
                <Bar dataKey="positive" name="Positive" stackId="reviews" fill={POSITIVE} stroke="#fff" strokeWidth={1} maxBarSize={24} isAnimationActive={false} />
                <Bar dataKey="negative" name="Negative" stackId="reviews" fill={NEGATIVE} stroke="#fff" strokeWidth={1} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
        </ResponsiveContainer>
    );
};

const RatingBars = ({ rows, scale, threshold }) => {
    const [showAll, setShowAll] = React.useState(false);
    const sorted = React.useMemo(() => [...rows].sort((a, b) => b.average - a.average || b.count - a.count), [rows]);
    const shown = showAll ? sorted : sorted.slice(0, MAX_BARS_SHOWN);
    const tooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const r = payload[0].payload;
        const counts = r.positive != null
            ? `${formatCount(r.count)} reviews · ${share(r.positive, r.count)}% positive`
            : `${formatCount(r.count)} ratings`;
        return <TooltipBox title={r.name} lines={[[null, `Average ${r.average.toFixed(2)} of ${scale}`], [null, counts]]} />;
    };
    return (
        <>
            <ResponsiveContainer width="100%" height={shown.length * 30 + 36}>
                <BarChart data={shown} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                    <CartesianGrid horizontal={false} stroke={GRID} />
                    <XAxis type="number" domain={[0, scale]} ticks={Array.from({ length: scale + 1 }, (_, i) => i)}
                        tick={{ fill: MUTED, fontSize: 12 }} tickLine={false} axisLine={{ stroke: AXIS }} />
                    <YAxis type="category" dataKey="name" width={200} tick={<NameTick />} axisLine={false} tickLine={false} interval={0} />
                    {threshold != null && <ReferenceLine x={threshold} stroke={AXIS} strokeWidth={1} />}
                    <ChartTooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={tooltip} />
                    <Bar dataKey="average" fill={SERIES} barSize={14} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                        <LabelList dataKey="average" position="right" formatter={(v) => Number(v).toFixed(1)} style={{ fill: TEXT_SECONDARY, fontSize: 12 }} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            {sorted.length > MAX_BARS_SHOWN && (
                <Button size="small" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? `Show top ${MAX_BARS_SHOWN}` : `Show all ${sorted.length}`}
                </Button>
            )}
        </>
    );
};

const ratingTable = (rows, noun, countLabel = 'Reviews') => ({
    columns: [
        { key: 'name', label: noun },
        { key: 'average', label: 'Average', align: 'right', format: (v) => Number(v).toFixed(2) },
        { key: 'count', label: countLabel, align: 'right', format: formatCount },
        ...(rows.some((r) => r.positive != null)
            ? [{ key: 'positive', label: 'Positive', align: 'right', format: (v, r) => `${share(v, r.count)}%` }]
            : []),
    ],
    rows: [...rows].sort((a, b) => b.average - a.average),
});

const otherScaleNote = (others) => {
    const n = others.reduce((sum, b) => sum + b.count, 0);
    const scale = others[0]?.scale === 10 ? '1–10' : 'smiley (1–5)';
    return `${formatCount(n)} ${n === 1 ? 'review' : 'reviews'} in this period used the ${scale} scale and ${n === 1 ? 'is' : 'are'} not included. The two scales are never mixed.`;
};

export const ReviewInsights = ({ filters, query, range, periodKey, configuredScale, threshold }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [collapsed, setCollapsed] = React.useState(readCollapsed);
    const filtersKey = JSON.stringify(filters || []);

    React.useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        const filter = {
            filters: filtersKey,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(query ? { query } : {}),
            ...(range?.start ? { start: range.start } : {}),
            ...(range?.end ? { end: range.end } : {}),
        };
        Promise.resolve(dataAPI(GET_ONE, 'review', { filter, urlSuffix: 'summary/', keepSessionOn403: true }))
            .then((res) => {
                if (!active) return;
                if (!isOk(res)) setError(serverErrorMessage(res, 'Couldn’t load the ratings overview'));
                else setData(res.data);
            })
            .catch((e) => { if (active) setError(String(e?.message || e)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [dataAPI, filtersKey, query, range?.start, range?.end]);

    const toggle = () => setCollapsed((v) => { writeCollapsed(!v); return !v; });
    const period = reviewPeriod(periodKey);
    const comparedTo = period.days ? `the previous ${period.days} days` : null;
    const { primary, others } = pickScaleBlock(data?.scales, configuredScale);
    const scale = primary?.scale;
    const lineAt = scale === configuredScale ? threshold : defaultThreshold(scale);
    const previous = primary?.previous;
    const bucket = data?.period?.bucket;
    const trendRows = primary ? primary.trend.map((t) => ({ ...t, label: bucketLabel(t.date, bucket) })) : [];
    const aspects = primary?.aspects || []; // an older backend sends none
    const aspectScale = data?.aspect_scale || 5;
    const barCards = 1 + (primary?.guides?.length > 0 ? 1 : 0) + (aspects.length > 0 ? 1 : 0);

    return (
        <Box sx={{ mx: { xs: 2, sm: 2 }, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={collapsed ? 0 : 1.5}>
                <Typography variant="h6" fontSize={18}>Ratings overview</Typography>
                <Typography variant="body2" color="text.secondary">{period.label}</Typography>
                <IconButton size="small" onClick={toggle} aria-label={collapsed ? 'Show the ratings overview' : 'Hide the ratings overview'} aria-expanded={!collapsed}>
                    {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
            </Box>
            <Collapse in={!collapsed} unmountOnExit>
                {loading && !data && <LinearProgress />}
                {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
                {data && !primary && <Typography color="text.secondary">No reviews in this period.</Typography>}
                {primary && (
                    <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }} aria-busy={loading}>
                        {others.length > 0 && <Alert severity="info" sx={{ mb: 1.5 }}>{otherScaleNote(others)}</Alert>}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatTile
                                    label="Average score"
                                    value={primary.average != null ? primary.average.toFixed(1) : '–'}
                                    unit={`of ${scale}`}
                                    icon={scale === 5 && primary.average != null ? (
                                        <img src={`/rating-emojis/rating-${Math.min(5, Math.max(1, Math.round(primary.average)))}.svg`} alt="" width={28} height={28} />
                                    ) : null}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatTile label="Reviews" value={formatCount(primary.count)}
                                    delta={changePercent(primary.count, previous?.count)} comparedTo={comparedTo} />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatTile label="Positive reviews" value={formatCount(primary.positive)}
                                    detail={`${share(primary.positive, primary.count)}% of reviews · ${lineAt} and up`}
                                    meter={share(primary.positive, primary.count)}
                                    delta={changePercent(primary.positive, previous?.positive)} goodWhenUp comparedTo={comparedTo} />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatTile label="Negative reviews" value={formatCount(primary.negative)}
                                    detail={`${share(primary.negative, primary.count)}% of reviews · below ${lineAt}`}
                                    delta={changePercent(primary.negative, previous?.negative)} goodWhenUp={false} comparedTo={comparedTo} />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <ChartCard
                                    title="Score breakdown"
                                    subtitle="Share of reviews at each rating"
                                    table={{
                                        columns: [
                                            { key: 'score', label: 'Rating' },
                                            { key: 'count', label: 'Reviews', align: 'right', format: formatCount },
                                            { key: 'share', label: 'Share', align: 'right', format: (_, r) => `${share(r.count, primary.count)}%` },
                                        ],
                                        rows: primary.distribution,
                                    }}
                                >
                                    <ScoreBreakdown block={primary} />
                                </ChartCard>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <ChartCard
                                    title="Positive and negative reviews"
                                    subtitle={bucket === 'month' ? 'Per month' : bucket === 'week' ? 'Per week' : 'Per day'}
                                    table={{
                                        columns: [
                                            { key: 'label', label: bucket === 'month' ? 'Month' : bucket === 'week' ? 'Week' : 'Day' },
                                            { key: 'positive', label: 'Positive', align: 'right', format: formatCount },
                                            { key: 'negative', label: 'Negative', align: 'right', format: formatCount },
                                        ],
                                        rows: trendRows,
                                    }}
                                >
                                    <Trend rows={trendRows} />
                                </ChartCard>
                            </Grid>
                            <Grid item xs={12} lg={barCards > 1 ? 6 : 12}>
                                <ChartCard
                                    title="Ratings by tour"
                                    subtitle={`Average score per tour with at least ${data.min_reviews_per_bar} reviews. The line marks positive (${lineAt} and up).`}
                                    table={ratingTable(primary.tours, 'Tour')}
                                >
                                    {primary.tours.length > 0
                                        ? <RatingBars rows={primary.tours} scale={scale} threshold={lineAt} />
                                        : <Typography variant="body2" color="text.secondary">No tour has {data.min_reviews_per_bar} reviews in this period yet.</Typography>}
                                </ChartCard>
                            </Grid>
                            {primary.guides.length > 0 && (
                                <Grid item xs={12} lg={6}>
                                    <ChartCard
                                        title="Ratings by guide"
                                        subtitle={`Guides tagged on the trip chat, with at least ${data.min_reviews_per_bar} reviews. A review counts for every guide on its trip.`}
                                        table={ratingTable(primary.guides, 'Guide')}
                                    >
                                        <RatingBars rows={primary.guides} scale={scale} threshold={lineAt} />
                                    </ChartCard>
                                </Grid>
                            )}
                            {aspects.length > 0 && (
                                // Only operators on the advanced review form have aspect ratings; always 1-5
                                <Grid item xs={12} lg={6}>
                                    <ChartCard
                                        title="Ratings by aspect"
                                        subtitle={`From the ${formatCount(primary.aspect_reviews)} reviews (${share(primary.aspect_reviews, primary.count)}%) that rated aspects. Aspects are rated 1–${aspectScale}.`}
                                        table={ratingTable(aspects, 'Aspect', 'Ratings')}
                                    >
                                        <RatingBars rows={aspects} scale={aspectScale} threshold={null} />
                                    </ChartCard>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

export default ReviewInsights;
